import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
 * Custom Checkbox Component
 */
@Component({
  selector: 'atx-switch',
  templateUrl: './switch.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => SwitchComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SwitchComponent
  implements ControlValueAccessor, OnInit, OnDestroy
{
  /**
   * Id for the input so the label can bind to the correct radio
   */
  @Input() public inputId: string;

  /**
   * When both checked and unchecked states need to have an 'active' appearance.
   */
  @Input() public biState: boolean;

  public control = new FormControl(false);

  private destroy$ = new Subject<void>();

  public ngOnInit() {
    this.listenChange();
  }

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Required for ControlValueAccessor implementation
   */
  public onChange: (checked: boolean) => void = () => {};
  public onTouch: (arg: boolean) => void = () => {};

  /**
   * Required for ControlValueAccessor implementation
   */
  public writeValue(obj: boolean): void {
    this.control.patchValue(obj, { emitEvent: false });
  }

  /**
   * Required for ControlValueAccessor implementation
   */
  public registerOnChange(fn: () => {}): void {
    this.onChange = fn;
  }

  /**
   * Required for ControlValueAccessor implementation
   */
  public registerOnTouched(fn: () => {}): void {
    this.onTouch = fn;
  }

  /**
   * set disabled state if the input status is changed
   */
  public setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.control.disable() : this.control.enable();
  }

  private listenChange() {
    this.control.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: boolean) => {
        this.onChange(value);
      });
  }
}
